import React, { ReactNode } from 'react';
import NextLink from 'next/link';
import styled from '@emotion/styled';
import { Colors } from '@zapier/design-system';
import { urlParser } from './linkStrategy';
import { BASE_PATH } from '../../constants/paths';
import { setHttpRefererFromStorage } from 'tracking/TrackingProvider';

type Props = {
  className?: string;
  color?: 'blue' | 'black';
  // The path inside pages directory. This is the only required prop.
  href: string;
  children: ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  testId?: string;
  rel?: string;
};

export const Link = ({
  className,
  color,
  href,
  children,
  onClick = () => {},
  testId,
  rel,
}: Props) => {
  const parsedUrl = urlParser(href);
  const { href: parsedHref, target } = parsedUrl;
  const linkRel = rel || parsedUrl.rel;
  // urlParser reliably outputs absolute blog URLs (converts fully qualified blog -> absolute blog)
  const isBlogLink = parsedHref.startsWith(BASE_PATH);

  const aProps = {
    color,
    target,
    className,
    onClick,
    'data-test-id': testId,
  } as const;

  function handleClick(e: React.MouseEvent) {
    setHttpRefererFromStorage();
    onClick(e);
  }

  if (isBlogLink) {
    const nextLinkHref =
      parsedHref === BASE_PATH ? '/' : parsedHref.replace(BASE_PATH, '');
    return (
      <NextLink href={nextLinkHref} passHref>
        <CustomA {...aProps} onClick={handleClick} rel={linkRel}>
          {children}
        </CustomA>
      </NextLink>
    );
  }

  return (
    <CustomA {...aProps} href={href} rel={linkRel}>
      {children}
    </CustomA>
  );
};

const CustomA = styled.a<{ color?: 'blue' | 'black' }>`
  color: ${({ color }) =>
    color === 'blue' ? `${Colors.blue500}` : `${Colors.black}`};
  text-decoration: none;
  &:hover,
  :focus,
  :focus-within {
    color: ${({ color }) =>
      color === 'blue' ? `${Colors.blue700}` : `${Colors.blue500}`};
    & > span {
      color: ${({ color }) =>
        color === 'blue' ? `${Colors.blue700}` : `${Colors.blue500}`};
    }
    cursor: pointer;
  }
`;
